<script>

    import { createEventDispatcher, onDestroy } from 'svelte'
    import { toast } from 'bulma-toast'

    import UsersService from '../$services/users.service.js'

    import InputText from '../$components/input.text.svelte'
    import Button from '../$components/button.svelte'

    const dispatch = createEventDispatcher()

    const RESEND_SECONDS = 60
    const MIN_PASSWORD = 8

    export let email = ''

    let step = 'request'
    let code = ''
    let password = ''
    let confirm = ''
    let loading = false
    let wait = 0
    let timer = null

    $: code = (code || '').replace(/\D/g, '').slice(0, 6)

    async function sendCode() {

        if (!email)
            return toast({ message: 'Ingresa tu correo electrónico', type: 'is-danger' })

        loading = true
        const resp = await UsersService.forgotPassword({ email })
        loading = false

        if (!resp.success)
            return toast({ message: resp.error.message, type: 'is-danger' })

        toast({ message: 'Si el correo está registrado, te enviamos un código', type: 'is-info' })

        step = 'reset'
        startWait()
    }

    async function resetPassword() {

        if (code.length !== 6)
            return toast({ message: 'El código debe tener 6 dígitos', type: 'is-danger' })

        if (password.length < MIN_PASSWORD)
            return toast({ message: `La contraseña debe tener mínimo ${MIN_PASSWORD} caracteres`, type: 'is-danger' })

        if (password !== confirm)
            return toast({ message: 'Las contraseñas no coinciden', type: 'is-danger' })

        loading = true
        const resp = await UsersService.resetPassword({ email, code, password })
        loading = false

        if (!resp.success)
            return toast({ message: resp.error.message, type: 'is-danger' })

        toast({ message: 'Contraseña actualizada, ya puedes iniciar sesión', type: 'is-success' })
        dispatch('back')
    }

    async function resend() {

        if (wait > 0 || loading)
            return

        loading = true
        const resp = await UsersService.forgotPassword({ email })
        loading = false

        if (!resp.success)
            return toast({ message: resp.error.message, type: 'is-danger' })

        toast({ message: 'Si el correo está registrado, te enviamos un nuevo código', type: 'is-info' })
        startWait()
    }

    function startWait() {

        clearInterval(timer)
        wait = RESEND_SECONDS

        timer = setInterval(() => {
            wait -= 1

            if (wait <= 0)
                clearInterval(timer)
        }, 1000)
    }

    onDestroy(() => clearInterval(timer))

</script>

{#if step === 'request'}
    <form class="recover-form" on:submit|preventDefault={sendCode} novalidate>
        <InputText
            label="Correo electrónico"
            bind:value={email}
            type="email"
            name="username"
            inputmode="email"
            autocomplete="username"
            placeholder="nombre@ejemplo.com"
            required
        />

        <div class="recover-form__actions">
            <Button
                type="submit"
                color="primary"
                icon="paper-plane"
                text="Enviar código"
                fullwidth
                {loading}
            />
        </div>

        <div class="recover-form__footer">
            <button type="button" class="recover-form__link" on:click={() => dispatch('back')}>
                Volver a iniciar sesión
            </button>
        </div>
    </form>
{:else}
    <form class="recover-form" on:submit|preventDefault={resetPassword} novalidate>
        <InputText
            label="Código de 6 dígitos"
            bind:value={code}
            name="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="000000"
            required
        />

        <InputText
            label="Nueva contraseña"
            bind:value={password}
            type="password"
            name="new-password"
            autocomplete="new-password"
            placeholder="Mínimo {MIN_PASSWORD} caracteres"
            required
        />

        <InputText
            label="Confirmar contraseña"
            bind:value={confirm}
            type="password"
            name="confirm-password"
            autocomplete="new-password"
            placeholder="Repite la contraseña"
            required
        />

        <div class="recover-form__actions">
            <Button
                type="submit"
                color="primary"
                icon="key"
                text="Cambiar contraseña"
                fullwidth
                {loading}
            />
        </div>

        <div class="recover-form__footer">
            <button type="button" class="recover-form__link" disabled={wait > 0 || loading} on:click={resend}>
                {wait > 0 ? `Reenviar código (${wait} s)` : 'Reenviar código'}
            </button>

            <button type="button" class="recover-form__link" on:click={() => dispatch('back')}>
                Volver a iniciar sesión
            </button>
        </div>
    </form>
{/if}

<style>
    .recover-form :global(.field:last-of-type) {
        margin-bottom: 0;
    }

    .recover-form__actions {
        margin-top: 0.875rem;
        padding-top: 0;
    }

    .recover-form__footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        margin-top: 1rem;
    }

    .recover-form__link {
        padding: 0;
        border: 0;
        background: none;
        cursor: pointer;
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--til-muted);
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    .recover-form__link:hover:not(:disabled) {
        color: var(--til-ink);
    }

    .recover-form__link:disabled {
        cursor: not-allowed;
        text-decoration: none;
        opacity: 0.6;
    }
</style>
